import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const monthlyData = [
  { month: 'Jan', revenue: 85000 },
  { month: 'Feb', revenue: 92000 },
  { month: 'Mar', revenue: 88000 },
  { month: 'Apr', revenue: 105000 },
  { month: 'May', revenue: 115000 },
  { month: 'Jun', revenue: 124500 },
];

const categoryBreakdown = [
  { category: 'Widgets', percentage: 45, color: '#008080' },
  { category: 'Services', percentage: 25, color: '#F4A261' },
  { category: 'Accessories', percentage: 18, color: '#003366' },
  { category: 'Support', percentage: 12, color: '#BBCDE2' },
];

const SampleAnalytics: React.FC = () => {
  const maxRevenue = Math.max(...monthlyData.map(d => d.revenue));

  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
        Analytics
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        Detailed analysis of your business performance
      </Typography>

      <Grid container spacing={3}>
        {/* Revenue Trend */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                Revenue Trend
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: 'success.main' }} />
                <Typography sx={{ color: 'success.main', fontWeight: 600 }}>
                  +18.2% YTD
                </Typography>
              </Box>
            </Box>

            {/* Simple Bar Chart */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 220, pt: 3 }}>
              {monthlyData.map((data) => (
                <Box key={data.month} sx={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 600, mb: 0.5 }}>
                    ${(data.revenue / 1000).toFixed(0)}k
                  </Typography>
                  <Box
                    sx={{
                      height: `${(data.revenue / maxRevenue) * 150}px`,
                      width: '100%',
                      bgcolor: 'primary.main',
                      borderRadius: '4px 4px 0 0',
                      transition: 'height 0.3s ease',
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                    {data.month}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Category Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700, mb: 3 }}>
              Revenue by Category
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {categoryBreakdown.map((cat) => (
                <Box key={cat.category}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'secondary.main' }}>
                      {cat.category}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                      {cat.percentage}%
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                    <Box
                      sx={{
                        width: `${cat.percentage}%`,
                        bgcolor: cat.color,
                        borderRadius: 1,
                        height: '100%',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Key Metrics */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {[
              { label: 'Customer Lifetime Value', value: '$2,450', change: '+12%' },
              { label: 'Customer Acquisition Cost', value: '$185', change: '-8%' },
              { label: 'Monthly Recurring Revenue', value: '$45,200', change: '+22%' },
              { label: 'Churn Rate', value: '2.1%', change: '-0.5%' },
            ].map((metric) => (
              <Grid item xs={12} sm={6} md={3} key={metric.label}>
                <Card sx={{ border: 1, borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {metric.label}
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 700, my: 1 }}>
                      {metric.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: metric.change.startsWith('+') || metric.change.startsWith('-0') ? 'success.main' : 'error.main',
                        fontWeight: 600,
                      }}
                    >
                      {metric.change} vs last period
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SampleAnalytics;
